import { useState, useEffect } from "react";
import Confetti from "react-confetti";
import { useParams } from "react-router";
import api from "../../services/api";
import { initializeAllPixels, setAdvancedMatching } from '../../utils/firePixel';
import { firePageView, firePurchaseWithEventId, getCurrentEventId } from '../../utils/pixelTracking';

// Interfaces para tipagem dos dados da API

interface OrderItem {
    id: string;
    productName: string;
    productImage: string;
    offerName: string;
    price: number;
    originalPrice: number;
    quantity: number;
    totalPrice: number;
}

interface Customer {
    id: string;
    name: string;
    email: string;
    document: string;
    phone: string;
    avatar: string;
}

interface OrderData {
    id: string;
    status: string;
    method: string;
    totalAmount: number;
    createdAt: string;
    updatedAt: string;
}

interface PaymentData {
    id?: string;
    status?: string;
    method?: string;
    grossAmount: number | null;
    transactionId?: string;
    createdAt?: string;
    paidAt?: string;
    installments: number;
    firstDigits: string;
    lastDigits: string;
    expirationDate: string;
    cardholderName: string;
}

interface SuccessResponse {
    status: string;
    data: {
        order: OrderData;
        customer: Customer;
        items: OrderItem[];
        payment: PaymentData | null;
        pixels?: any[]; // Adicionando o campo pixels que existe na resposta da API
    };
}

const SuccessPage = () => {
    const { id } = useParams<{ id: string }>();
    const [showConfetti, setShowConfetti] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [orderData, setOrderData] = useState<SuccessResponse | null>(null);

    // Função para mapear método de pagamento
    const getPaymentMethodLabel = (method: string) => {
        switch (method) {
            case 'card':
                return 'Cartão de Crédito';
            case 'pix':
                return 'PIX';
            default:
                return 'Cartão de Crédito';
        }
    };

    // Função para buscar os dados do pagamento Boleto seguindo o padrão do projeto
    const fetchSuccessData = async () => {
        try {
            setLoading(true);

            // Verificar se temos o id da URL
            if (!id) {
                setError("ID do pedido não encontrado.");
                setLoading(false);
                return;
            }

            // Preparar o URL
            const url = `/api/sales/order/${id}`;

            // Chamada para a API
            const response = await api.get(url);

            // Verificar se a resposta tem o formato esperado
            if (response.data && response.data.status === 'success') {
                setOrderData(response.data);
            } else {
                console.error("Erro ao carregar dados da transação:", response.data);
                setError("Não foi possível carregar os dados da transação.");
            }
        } catch (err) {
            console.error("Erro ao buscar dados da transação:", err);
            setError("Não foi possível carregar os dados da transação. Por favor, tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    // Efeito para chamar a função de busca quando o componente montar
    useEffect(() => {
        fetchSuccessData();
    }, [id]);

    // Efeito para disparar o pixel de compra quando os dados do pedido estiverem disponíveis
    useEffect(() => {
        if (orderData && orderData.data && orderData.data.order && !loading) {
            // Extrair dados relevantes para o pixel
            const order = orderData.data.order;
            const payment = orderData.data.payment;
            const items = orderData.data.items;
            const orderId = order.id;
            
            // Usar os pixels já disponíveis na resposta da API
            const pixels = orderData.data.pixels || [];
            
            // Verificar se Purchase já foi disparado (dedupe)
            const existingEventId = getCurrentEventId('purchase', orderId);
            
            // Purchase só para pedido PAGO (boleto/PIX ainda pendente não é compra).
            // Mesmo event_id do servidor (purchase_<order.id>): o Facebook conta uma vez.
            const isPaid = ['approved', 'paid'].includes(order.status);

            if (!existingEventId && isPaid) {
                // Primeira vez disparando Purchase
                console.log('[Success] Disparando Purchase pela primeira vez');
                
                // Inicializa os pixels e dispara PageView + Purchase
                // O pixel da Sellyo sempre será incluído automaticamente
                initializeAllPixels(pixels).then(() => {
                    // Dados do comprador para o pixel casar a conversão (Advanced Matching)
                    const customer = orderData.data.customer || {};
                    setAdvancedMatching(pixels, {
                        email: customer.email,
                        phone: customer.phone !== '--' ? customer.phone : undefined,
                        name: customer.name,
                        document: customer.document,
                    });

                    // 1. Disparar PageView (sempre no primeiro load da página de sucesso)
                    firePageView(pixels, orderId);
                    
                    // 2. Disparar Purchase com event_id. `paymentMethod` aplica a % de PIX/boleto do pixel.
                    // Boleto: pixel com "Purchase para boleto gerado" já contou na emissão — não conta de novo.
                    const purchasePixels = order.method === 'boleto'
                        ? pixels.filter((p: any) => p.enableBoletoEvent !== true)
                        : pixels;
                    firePurchaseWithEventId(purchasePixels, {
                        value: order.totalAmount,
                        currency: "BRL",
                        transactionId: payment?.transactionId || order.id,
                        paymentMethod: order.method,
                        content_type: "product",
                        content_ids: items.map(item => item.id),
                        contents: items.map(item => ({ id: item.id, quantity: item.quantity || 1, item_price: item.price })),
                        num_items: items.length,
                    }, orderId);
                });
            } else {
                console.log('[Success] Purchase já foi disparado anteriormente com event_id:', existingEventId);
            }
        }
    }, [orderData, loading]);

    // Efeito para confetti
    useEffect(() => {
        const timer = setTimeout(() => setShowConfetti(false), 5000);
        return () => clearTimeout(timer);
    }, []);

    // Se estiver carregando, mostrar spinner
    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                        <span className="sr-only">Carregando...</span>
                    </div>
                    <p>Carregando dados da transação...</p>
                </div>
            </div>
        );
    }

    // Se houver erro, mostrar mensagem
    if (error || !orderData) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center max-w-md p-6 bg-white shadow-lg rounded-lg">
                    <div className="text-red-500 text-8xl mb-4">✗</div>
                    <h2 className="text-xl font-semibold mb-4">Erro ao carregar transação</h2>
                    <p className="mb-6">{error || "Dados da transação não encontrados."}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={200}
                />
            )}
            <div className="max-w-7xl mx-auto py-3 pr-3 pl-3">
                <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6">

                    <div className="flex flex-col items-center gap-2 mb-8">
                        <div className="flex items-center gap-2">
                            <span className="text-green-600 text-xl">✔</span>
                            <h1 className="text-xl font-semibold text-green-600">Parabéns!</h1>
                        </div>
                        <p className="text-sm">
                            Sua compra foi aprovada e está sendo processada.
                        </p>
                    </div>


                    <div className="w-full p-4 rounded-lg bg-gray-100 text-gray-800 text-center mb-8">
                        <p className="text-sm font-medium">O código da sua transação é:</p>
                        <div className="pt-3">
                            <p className="text-lg font-semibold text-gray-800">{orderData.data.order.id.toUpperCase()}</p>
                        </div>
                    </div>

                    {/* Mensagem informativa */}
                    <div className="space-y-2 mb-4 text-start">
                        <p className="text-sm">
                            Olá, {orderData.data.customer.name}!
                        </p>
                        <p className="text-sm">
                            Sua compra foi aprovada e está sendo processada. Nos próximos minutos você receberá um e-mail com os dados para acessar este produto.
                        </p>
                    </div>

                    <div className="w-full p-4 rounded-lg bg-yellow-100 text-yellow-800 text-center mt-8 mb-8">
                        <p className="text-sm font-medium">Caso não encontre o e-mail, verifique também sua caixa de spam.</p>
                    </div>
                </div>

                <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6">
                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Informações do pedido */}
                        <div className="flex-1">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Resumo do pedido</h3>
                                <div className="space-y-4">
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Data do pedido:</span> {new Date(orderData.data.order.createdAt).toLocaleString('pt-BR')}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Número do pedido:</span> {orderData.data.order.id.substring(24, 36).toUpperCase()}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Código do pedido:</span> {orderData.data.order.id.substring(0, 8).toUpperCase()}
                                    </p>
                                    {orderData.data.payment && orderData.data.payment.transactionId && (
                                        <p className="text-xs text-gray-600">
                                            <span className="font-medium">Código da transação:</span> {orderData.data.payment.transactionId.substring(0, 8)}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Forma de Pagamento:</span> {getPaymentMethodLabel(orderData.data.order.method)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Resumo do Pedido */}
                        <div className="flex-1">
                            <div className="space-y-6">
                                <div>
                                    {orderData.data.items && (
                                        <div className="flex flex-col">
                                            {orderData.data.items.map((item, index) => (
                                                <div key={index} className="flex items-center my-4">
                                                    {/* Placeholder para imagem do produto */}
                                                    <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center mr-4">
                                                        <img src={item.productImage} alt={item.productName} className="w-full h-full object-cover" />
                                                    </div>
                                                    <div className="flex-1">
                                                        {/* Aqui poderia mostrar o nome do produto, se disponível */}
                                                        <p className="text-sm">{item.productName || 'Produto'}</p>
                                                    </div>
                                                    <p className="text-sm">
                                                        R$ {item.price ? Number(item.price).toFixed(2) :
                                                            orderData.data.order.totalAmount ? Number(orderData.data.order.totalAmount).toFixed(2) : '0,00'}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <hr className="my-4" />

                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <p className="text-base font-semibold">Total pago</p>
                                            <p className="text-base font-semibold">
                                                R$ {Number(orderData.data.order.totalAmount || 0).toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6 justify-between">
                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Ajuda */}
                        <div className="flex-1">
                            <div className="space-y-2">
                                <h3 className="text-sm">Precisa de ajuda?</h3>
                                <p className="text-xs text-gray-600">Entre em contato com o produtor.</p>
                            </div>
                        </div>

                        {/* Contato do Produtor */}
                        <div className="flex-1">
                            <div className="space-y-2">
                                <p className="text-xs text-gray-600">
                                    <span className="font-medium">Nome:</span> {orderData.data.customer.name || 'N/A'}
                                </p>
                                <p className="text-xs text-gray-600">
                                    <span className="font-medium">E-mail:</span> {orderData.data.customer.email || 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SuccessPage;